import { get } from 'lodash';
import moment from 'moment';
import ApiConstants from '../../../src/config/apiConstants';
import {
	DAILY_LIMIT_ERROR_CODE,
	TRANSACTION_LIMIT_ERROR_CODE,
	authoriserType,
	REPEATING_PAYMENT_LIMIT_ERROR_CODE,
} from '../../../src/config';

const getCalenderDates = () =>
	[...Array(30).keys()].map((num) => ({
		submission: moment().add(num, 'days').format('YYYY-MM-DD'),
		settlement: moment()
			.add(num + 2, 'days')
			.format('YYYY-MM-DD'),
	}));
export const authorisationObligateStub = (id) => ({
	id,
	code: 200,
	body: {
		source_id: id,
		status: ApiConstants.obligate, // status - allow, decline or obligate
	},
});

export const authorisationObligateUserStub = (id) => ({
	id,
	code: 200,
	body: {
		source_id: id,
		status: ApiConstants.obligate, // status - allow, decline or obligate
		ben_id_present: false,
		user_list: [
			// optional list of approvals will be returned when status = "obligate"
			// Uncomment first user here to trigger self approval logic as this ID matches user ID
			// of logged in user in stubs
			// {
			// 	approver_name: 'Self Approver One',
			// 	approver_id: '1234567890',
			// 	approver_type: authoriserType.single,
			// },
			{
				approver_name: 'Single Approver one',
				approver_id: '0000000000',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Single Approver two',
				approver_id: '1111111111',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Single Approver three',
				approver_id: '2222222222',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Single Approver four',
				approver_id: '3333333333',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Multi Approver one',
				approver_id: '4444444444',
				approver_type: authoriserType.multi,
			},
			{
				approver_name: 'Multi Approver two',
				approver_id: '5555555555',
				approver_type: authoriserType.multi,
			},
			{
				approver_name: 'Multi Approver three',
				approver_id: '6666666666',
				approver_type: authoriserType.multi,
			},
			{
				approver_name: 'Multi Approver four',
				approver_id: '7777777777',
				approver_type: authoriserType.multi,
			},
		],
	},
});

export const moreThanOneAuthorisationObligateUserStub = (id) => ({
	id,
	code: 200,
	body: {
		source_id: id,
		status: ApiConstants.obligate, // status - allow, decline or obligate
		ben_id_present: false,
		user_list: [
			// optional list will be returned when additional approval is required
			// Uncomment first user here to trigger self approval logic as this ID matches user ID
			// of logged in user in stubs
			// {
			// 	approver_name: 'Self Approver One',
			// 	approver_id: '1234567890',
			// 	approver_type: authoriserType.single,
			// },
			{
				approver_name: 'Single Approver one',
				approver_id: '0000111111',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Single Approver two',
				approver_id: '0000111112',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Single Approver three',
				approver_id: '1234567891',
				approver_type: authoriserType.single,
			},
			{
				approver_name: 'Multi Approver one',
				approver_id: '1000111111',
				approver_type: authoriserType.multi,
			},
			{
				approver_name: 'Multi Approver two',
				approver_id: '2000111112',
				approver_type: authoriserType.multi,
			},
			{
				approver_name: 'Multi Approver three',
				approver_id: '3234567891',
				approver_type: authoriserType.multi,
			},
		],
	},
});

export const authorisationSuccessStub = (id) => ({
	id,
	code: 200,
	body: {
		source_id: id,
		status: ApiConstants.allow, // status - allow, decline or obligate
	},
});

export const authorisationDailyLimitExceededStub = (id) => ({
	id,
	code: 409,
	body: {
		error: {
			id: 'EACE598E-5186-429B-85EE-EFCECFD1CBF3',
			code: DAILY_LIMIT_ERROR_CODE,
			message:
				'A payment you’ve set up will take you over your daily payment limit. Please go back and edit or remove a payment on the following day(s):',
		},
	},
});

export const authorisationTransactionLimitExceededStub = (id) => ({
	id,
	code: 409,
	body: {
		error: {
			id: 'F2238D40-DDEC-4E5F-8C9D-01296DFC6C67',
			code: TRANSACTION_LIMIT_ERROR_CODE,
			message: 'This exceeds the maximum amount of money you can move online per transaction.',
		},
	},
});

export const authorisationFailureStub = (id) => ({
	id,
	code: 500,
	body: {
		error: 'Sorry something went wrong',
	},
});

export const getAuthorisationStub = (body) => {
	const paymentIds = body.batch_list.map((item) => item.id);
	const paymentValues = body.batch_list.map((item) => get(item, 'body.amount.value', 0));

	return {
		apiResponses: paymentIds.map((id, index) => {
			if (paymentValues[index] > 10000000) return authorisationTransactionLimitExceededStub(id);
			if (index === 4) return authorisationObligateStub(id);
			if (index === 3) return authorisationSuccessStub(id);
			if (index === 2) return authorisationObligateStub(id);
			if (index === 1) return authorisationObligateUserStub(id);
			if (index === 0) return authorisationObligateUserStub(id);

			if ((index + 1) % 3 === 0) return authorisationObligateStub(id);
			if ((index + 1) % 2 === 0) return authorisationObligateUserStub(id);

			// Change returned stub here to return different responses
			return authorisationObligateUserStub(id);
		}),
	};
};

export const getDailyLimitAuthorisationStub = (body) => {
	const paymentIds = body.batch_list.map((item) => item.id);

	return {
		apiResponses: paymentIds.map((id) => authorisationSuccessStub(id)),
	};
};

export const repeatingPaymentAuthorisationSuccessStub = (id) => ({
	source_id: id,
	status: 'success',
});

export const repeatingPaymentAuthorisationTransactionLimitFailureStub = (id) => ({
	error: {
		id,
		code: TRANSACTION_LIMIT_ERROR_CODE,
		message: 'This amount exceeds your single payment limit. Please contact us if you wish to discuss this further.',
	},
});

export const repeatingPaymentAuthorisationDailyLimitFailureStub = (id) => ({
	error: {
		id,
		code: DAILY_LIMIT_ERROR_CODE,
		message: 'This amount exceeds your daily payment limit. Please contact us if you wish to discuss this further.',
	},
});

export const repeatingPaymentAuthorisationLimitFailureStub = (id) => ({
	error: {
		id,
		code: REPEATING_PAYMENT_LIMIT_ERROR_CODE,
		message: 'This exceeds the maximum amount of money you can move in a repeating payment.',
	},
});

export const repeatingPaymentAuthorisationObligateStub = (id) => ({
	source_id: id,
	status: ApiConstants.obligate,
	user_list: [
		// optional list will be returned when additional approval is required
		// Uncomment first user here to trigger self approval logic as this ID matches user ID
		// of logged in user in stubs
		// {
		// 	approver_name: 'Self Approver One',
		// 	approver_id: '1234567890',
		// 	approver_type: authoriserType.single,
		// },
		{
			approver_name: 'Single Approver one',
			approver_id: '0000111111',
			approver_type: authoriserType.single,
		},
		{
			approver_name: 'Single Approver two',
			approver_id: '0000111112',
			approver_type: authoriserType.single,
		},
		{
			approver_name: 'Single Approver three',
			approver_id: '1234567891',
			approver_type: authoriserType.single,
		},
		{
			approver_name: 'Multi Approver one',
			approver_id: '1000111111',
			approver_type: authoriserType.multi,
		},
		{
			approver_name: 'Multi Approver two',
			approver_id: '2000111112',
			approver_type: authoriserType.multi,
		},
		{
			approver_name: 'Multi Approver three',
			approver_id: '3234567891',
			approver_type: authoriserType.multi,
		},
	],
});

export const getRepeatingPaymentAuthorisationStub = (body) => repeatingPaymentAuthorisationObligateStub(body.source_id);

export const getRepeatingPaymentConfirmedStub = (body) => ({
	id: body.source_id,
	status: 'success',
});

export const getRepeatingPaymentConfirm4pmFailureStub = () => ({
	error: {
		id: '290678FE-A749-4C9D-AE22-165FFB007410',
		code: '1001',
		message:
			"Sorry as it's after 4pm you cannot make a payment for the same or next business day. Please amend the date to continue with your payment.",
	},
});

export const beneficiaryStub = (id) => ({
	id,
	code: 201,

	apiResponses: [
		{
			id: '48e61be0-8420-11e8-8f9b-f3b769cf8339',
			code: 201,
			body: {
				id: '628b34c6-3937-4a0a-912d-d64a626732ef',
				name: 'B Instant Savings',
				reference: 'Some ref',
				account_id: 'c73803b9-21d7-4303-943c-d29eda332e84',
				display_name: 'B Instant Savings',
			},
		},
	],
});

export const addPayeeStub = (request) => ({
	id: `8902e720-93e2-8932-bd93-bd93-289faa747${Math.round(Math.random() * 999)}`,
	code: 201,
	...request,
});

export const addPayeeStubInvalidAccount = () => ({
	id: `8902e720-93e2-8932-bd93-bd93-289faa747${Math.round(Math.random() * 999)}`,
	coded_exceptions: [{ error_code: '027', error_status: 'Invalid Account' }],
});

export const addInternationalPayeeStub = (request) => ({
	id: `8902e720-93e2-8932-bd93-bd93-289faa747${Math.round(Math.random() * 999)}`,
	code: 201,
	...request,
});

export const transactionStub = (id) => ({
	id,
	code: 200,
	body: {
		id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
		source_id: id,
		status: 'success',
		code: '000',
		message: 'Your payment has been successful and may take up to 2 hours to complete.',
	},
});

export const transactionFailedStub = (id) => ({
	id,
	code: 500,
	body: {
		id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
		source_id: id,
		status: 'error',
		code: '000',
		message: 'Your payment has failed.',
	},
});

export const transactionInsufficientFundsStub = (id) => ({
	id,
	code: 400,
	body: {
		error: {
			id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
			code: '074',
			message: "You haven't got enough money to make that payment. How about a smaller amount?",
		},
	},
});

export const transactionOnHoldStub = (id) => ({
	id,
	code: 409,
	body: {
		error: {
			id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
			code: '70105',
			message:
				"As a security measure, your payment's been referred to our team for verification. Please contact us on 0800 121 4253 (+44 113 244 0926 if outside the UK) to verify your payment. We're here Monday to Sunday 8am to 9pm. Delays are usually minimal and we may even authorise your payment without having to verify it with you. Once your payment's been verified, it'll be processed and sent as usual, subject to available funds.",
		},
	},
});

export const transactionFasterPaymentsOnHoldStub = (id) => ({
	id,
	code: 409,
	body: {
		error: {
			id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
			code: '105',
			message:
				"As a security measure, your payment's been referred to our team for verification. Please contact us on 0800 121 4253 (+44 113 244 0926 if outside the UK) to verify your payment. We're here Monday to Sunday 8am to 9pm. Delays are usually minimal and we may even authorise your payment without having to verify it with you. Once your payment's been verified, it'll be processed and sent as usual, subject to available funds.",
		},
	},
});

export const transactionSoftTokenStub = (id) => ({
	id,
	code: 401,
	body: {
		verification_type: 'SOFT',
		trust_level: '40',
	},
});

export const transactionhardTokenStub = (id) => ({
	id,
	code: 401,
	body: {
		verification_type: 'HARD',
		trust_level: '40',
	},
});

export const transactionRejectedStub = (id) => ({
	id,
	code: 422,
	body: {
		id,
		code: '9384',
		message: 'Insufficient funds',
		metadata: {
			available_funds: 7.3,
		},
	},
});

export const transactionAdminAuthStub = (id) => ({
	id,
	code: 401,
	body: {
		id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
		source_id: id,
		verification_type: 'HARD',
		status: ApiConstants.obligate,
		code: '000',
		message: 'Your payment requires authorisation.',
	},
});

export const transactionWithResponse404Stub = (id) => ({
	id,
	code: 404,
	body: {
		id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
		error: {
			id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
			code: '027',
			message: 'Something went wrong!!',
		},
	},
});

export const getTransactionStub = (body) => {
	const paymentIds = body.batch_list.map((item) => item.id);

	return {
		apiResponses: paymentIds.map((id, index) => {
			if (index === 7) return transactionInsufficientFundsStub(id);
			if (index === 6) return transactionFailedStub(id);
			if (index === 5) return transactionWithResponse404Stub(id);
			if (index === 4) return transactionRejectedStub(id);
			if (index === 3) return transactionSoftTokenStub(id);
			if (index === 2) return transactionhardTokenStub(id);
			if (index === 1) return transactionOnHoldStub(id);
			if (index === 0) return transactionStub(id);

			if ((index + 1) % 3 === 0) return transactionhardTokenStub(id);
			if ((index + 1) % 2 === 0) return transactionAdminAuthStub(id);

			return transactionStub(id);
		}),
	};
};

export const storeTransactionStub = (id) => ({
	id,
	code: 202,
	body: null,
});

export const storeTransaction4pmFailureStub = (id) => ({
	id,
	code: 409,
	body: {
		error: {
			code: '1001',
			id: 'e3b252ab-3ca2-4c03-bd32-f7b0b61229f8',
			message:
				"Sorry as it's after 4pm you cannot make a payment for the same or next business day. Please amend the date to continue with your payment.",
		},
	},
});

export const getStoreTransactionStub = (body) => {
	const paymentIds = body.batch_list.map((item) => item.id);

	return {
		apiResponses: paymentIds.map((id) => storeTransactionStub(id)),
	};
};

export const getBatchReviewStub = (body) => ({
	apiResponses: body.batch_list.map((item) => moreThanOneAuthorisationObligateUserStub(item.id)),
});

export const batchSubmitDailyLimitFailure = {
	source_id: 'f07a634d-1f9f-4297-8988-d10fb1f447e8',
	batch_id: '20037855-f07c-4298-80c6-9f5a9a5e966a',
	status: ApiConstants.decline,
	error: {
		code: DAILY_LIMIT_ERROR_CODE,
		message: 'some such message',
	},
};

export const batchSubmitObligate = {
	source_id: 'f07a634d-1f9f-4297-8988-d10fb1f447e8',
	batch_id: '20037855-f07c-4298-80c6-9f5a9a5e966a',
	verification_type: 'HARD',
	status: ApiConstants.obligate,
	payments: [
		{
			status: 'obligate',
			code: '999',
			id: '81767ef0-4987-11e9-a1f6-9fc6792bd826',
			source_id: '81767ef0-4987-11e9-a1f6-9fc6792bd826',
		},
		{
			status: 'obligate',
			code: '999',
			id: 'a1080220-4987-11e9-a1f6-9fc6792bd826',
			source_id: 'a1080220-4987-11e9-a1f6-9fc6792bd826',
		},
	],
};

export const batchSubmitSuccess = () => ({
	status: ApiConstants.success,
	source_id: '8b3b0540-4c26-11e9-92a5-27a49a0f51c2',
	batch_id: '69666fe0-4c26-11e9-92a5-27a49a0f51c2',
	payments: [
		{
			status: 'failed',
			code: '3105',
			id: '76170880-4c26-11e9-92a5-27a49a0f51c2',
			source_id: '76170880-4c26-11e9-92a5-27a49a0f51c2',
		},
		{
			status: 'success',
			code: '0000',
			id: '72aa1c50-4c26-11e9-92a5-27a49a0f51c2',
			source_id: '72aa1c50-4c26-11e9-92a5-27a49a0f51c2',
		},
		{
			status: 'success',
			code: '0000',
			id: '6f6c2f60-4c26-11e9-92a5-27a49a0f51c2',
			source_id: '6f6c2f60-4c26-11e9-92a5-27a49a0f51c2',
		},
		{
			status: 'success',
			code: '0000',
			id: '69666fe1-4c26-11e9-92a5-27a49a0f51c2',
			source_id: '69666fe1-4c26-11e9-92a5-27a49a0f51c2',
		},
	],
});

export const timeoutResponse = {
	timestamp: 1583230266177,
	status: 500,
	error: 'Internal Server Error',
	message: 'Read timed out',
	correlation_id: 'c9874121-eaac-4fa3-6714-20ecccbc9392',
};

// export const getBatchSubmitStub = body => {
export const getBatchSubmitStub = () => {
	// return timeoutResponse;
	const result = true || Math.floor(Math.random() * Math.floor(2));
	if (result) {
		// return batchSubmitSuccess(body);
		return batchSubmitObligate;
	}
	return batchSubmitObligate;
};

export const calendarStub = { dates: getCalenderDates() };

export const getBacsTransactionStub = (id) => ({
	id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
	source_id: id,
	status: 'success',
	code: '000',
	message: 'New copy TBD',
});

export const getBacsTransactionHardAuthStub = (id) => ({
	id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
	source_id: id,
	status: 'obligate',
	code: '000',
	message: 'New copy TBD',
	verification_type: 'HARD',
});

export const getBacsTransactionSoftAuthStub = (id) => ({
	id: '6302e720-03e3-4832-bd93-2b5faa7479d5',
	source_id: id,
	status: 'obligate',
	code: '000',
	message: 'New copy TBD',
	verification_type: 'SOFT',
});

export const getBacsTransaction3rdPartyAuthStub = () => ({ trust_level: '40' });

export const getBacsTransactionDeviceNotEnrolledStub = () => ({
	error: {
		code: '404',
		message: 'Customer does not have an enrolled device',
		id: '80492e75-fdbe-4182-a5e7-cff13cea5eb2',
	},
});

export const getChapsDeadlineMissedStub = (id) => ({
	id,
	code: 409,
	body: {
		error: {
			id,
			code: '2002',
			message: 'Chaps deadline missed',
		},
	},
});

export const getBacsDeadlineMissedStub = (id) => ({
	error: {
		id,
		code: '1010',
		message:
			"Bacs deadline missed. As this payment's being submitted after 5:10pm, it'll now be submitted to Bacs on the next available working day. You can see when your payment will now be submitted and settled below",
	},
});

export const calendarCutOffMissed = {
	cut_offs: [
		{
			type: 'BACS',
			start: '0',
			end: '61800',
			next_available_date: moment().add(4, 'day').format('YYYY-MM-DD'),
		},
		{
			type: 'SO',
			start: '0',
			end: '57600',
			next_available_date: moment().add(4, 'day').format('YYYY-MM-DD'),
		},
		{
			type: 'CHAPS',
			start: '0',
			end: '61200',
			next_available_date: moment().add(4, 'day').format('YYYY-MM-DD'),
		},
	],
	current_time: '2018-11-23 10:24:57.449',
};

export const getPayeeSrdCheckStub = () => ({
	code: 200,
	srd_check_outcome: {
		cop_check_required: true,
		scheme_name: 'SortCodeAccountNumber',
		identification: '89896789896700',
	},
});
